/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { BLUE_200, CLEMENTINE_200, GRAY_200, PURPLE_200, YELLOW_300 } from 'cio/utils/colors';

// courtesy of Octopus component

interface ObjectsFlowChartArgs {
  container: HTMLDivElement;
}

const PATH_SAFETY_MARGIN = 10;

// Generates a "d" string for a <path> element, by:
// 1. Moving to the start coordinates
// 2. Drawing a Bezier curve for the first half of the path
// 3. Drawing the second half of the path as a "smooth curve", which simply extends the previous Bezier curve
// https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/d#cubic_b%C3%A9zier_curve
function generatePathString(startCoordinates = { x: 0, y: 0 }, endCoordinates = { x: 0, y: 0 }): string {
  let startX = startCoordinates.x;
  let startY = startCoordinates.y;

  let endX = endCoordinates.x;
  let endY = endCoordinates.y;

  // We anchor control points at thirds of the way along width of the available
  // space
  let horizontalBezierControlOffset = (endX - startX) / 3;

  let moveCommand = `M${startX},${startY}`;

  let bezierControlPoints = {
    x1: Math.round(startX + horizontalBezierControlOffset),
    y1: Math.round(startY),
    x2: Math.round(startX + (endX - startX) / 2 - 20), // 20 is an extra offset which was found experimentally, for visual balance
    y2: Math.round(startY),
    x: Math.round(startX + (endX - startX) / 2),
    y: Math.round(startY + (endY - startY) / 2),
  };
  let bezierCommand = `C${bezierControlPoints.x1},${bezierControlPoints.y1} ${bezierControlPoints.x2},${bezierControlPoints.y2} ${bezierControlPoints.x},${bezierControlPoints.y}`;

  let smoothCurveControlPoints = {
    x2: Math.round(startX + horizontalBezierControlOffset * 2),
    y2: Math.round(endY),
    x: Math.round(endX),
    y: Math.round(endY),
  };
  let smoothCurveCommand = `S${smoothCurveControlPoints.x2},${smoothCurveControlPoints.y2} ${smoothCurveControlPoints.x},${smoothCurveControlPoints.y}`;

  return `${moveCommand} ${bezierCommand} ${smoothCurveCommand}`;
}

enum StrokeColors {
  purple = 'purple',
  clementine = 'clementine',
  yellow = 'yellow',
  blue = 'blue',
  gray = 'gray',
}

const STROKE_SETTINGS = {
  [StrokeColors.purple]: {
    color: PURPLE_200,
    dasharray: '0 0',
    width: '3',
  },
  [StrokeColors.clementine]: {
    color: CLEMENTINE_200,
    dasharray: '0 0',
    width: '3',
  },
  [StrokeColors.yellow]: {
    color: YELLOW_300,
    dasharray: '0 0',
    width: '3',
  },
  [StrokeColors.blue]: {
    color: BLUE_200,
    dasharray: '0 0',
    width: '3',
  },
  [StrokeColors.gray]: {
    color: GRAY_200,
    dasharray: '0 0',
    width: '3',
  },
};

function generateStrokeSettings(type: StrokeColors): StrokeSettings {
  return STROKE_SETTINGS[type] ?? STROKE_SETTINGS[StrokeColors.gray];
}

function generatePathEdgeCoordinates({
  containerRect,
  elementRect,
  targetElementRect,
  sourceYOffset,
  targetYOffset,
  horizontalLine,
}: {
  containerRect: DOMRect;
  elementRect: DOMRect;
  targetElementRect: DOMRect;
  sourceYOffset: number;
  targetYOffset: number;
  horizontalLine: boolean;
}) {
  let xOffset = containerRect.left;
  let yOffset = containerRect.top;

  let startCoordinates = {
    x: Math.round(elementRect.right - xOffset - PATH_SAFETY_MARGIN),
    y: Math.round(elementRect.top + elementRect.height / 2 - yOffset + sourceYOffset),
  };

  let endCoordinates = {
    x: Math.round(targetElementRect.left - xOffset + PATH_SAFETY_MARGIN),
    y: horizontalLine
      ? startCoordinates.y
      : Math.round(targetElementRect.top + targetElementRect.height / 2 - yOffset + targetYOffset),
  };

  return {
    start: startCoordinates,
    end: endCoordinates,
  };
}

function generateDotSettings({
  id,
  pathCoordinates,
  color,
}: {
  id: string;
  pathCoordinates: {
    start: {
      x: number;
      y: number;
    };
    end: {
      x: number;
      y: number;
    };
  };
  color: string;
}): PathDotSettings {
  return {
    id: `${id}_dot`,
    x: pathCoordinates.start.x,
    y: pathCoordinates.start.y,
    pathId: '',
    color,
  };
}

export type StrokeSettings = {
  color: string;
  dasharray: string;
  width: string;
};

export type PathSettings = {
  id: string;
  d: string;
  stroke: StrokeSettings;
};

export type PathDotSettings = {
  id: string;
  x: number;
  y: number;
  pathId: string;
  color: string;
};

export default class ObjectsFlowChart extends Component<ObjectsFlowChartArgs> {
  svgContainerElement: HTMLDivElement | null = null;
  resizeObserver: ResizeObserver | null = null;

  @action
  didInsertSVGContainer(svgContainerElement: HTMLDivElement) {
    this.svgContainerElement = svgContainerElement;

    this.setSVGSize();
    this.attachResizeObserver();
  }

  willDestroy() {
    super.willDestroy();

    this.resizeObserver?.disconnect();
  }

  @tracked svgSize = { width: 0, height: 0 };

  @action
  setSVGSize() {
    if (this.svgContainerElement == null) {
      return;
    }

    let { width, height } = this.svgContainerElement.getBoundingClientRect();
    this.svgSize = { width, height };
  }

  @action
  attachResizeObserver() {
    if (this.svgContainerElement == null) {
      return;
    }

    this.resizeObserver = new ResizeObserver(() => requestAnimationFrame(this.onResize));
    this.resizeObserver.observe(this.svgContainerElement);
  }

  @action
  onResize() {
    this.setSVGSize();
    this.generatePathsAndDots();
  }

  @tracked paths: PathSettings[] = [];
  @tracked pathDots: PathDotSettings[] = [];

  generatePathsAndDots() {
    if (this.svgContainerElement == null) {
      return;
    }

    let { container } = this.args;
    let containerRect = container.getBoundingClientRect();

    let paths: PathSettings[] = [];
    let pathDots: PathDotSettings[] = [];

    let flowElements = Array.from(container.querySelectorAll('.js-flow-item'));

    // use Map for element "rect" caching
    let elementRectMap = new Map();

    // reverse so first flow is on top of lower ones
    flowElements.reverse().forEach((element, eIndex) => {
      let id = element.getAttribute('data-flow-id');
      let targets = element.getAttribute('data-flow-targets') ?? '';
      let horizontalLine = element.hasAttribute('data-flow-horizontal-line');

      // parse targets CSV-like format: "targetId, enabledEnumString, strokeColor, sourceYOffset, targetYOffset"
      // with ";" separator
      let flowTargets = targets.split(';').filter((item) => item.trim());

      flowTargets.forEach((target, tIndex) => {
        let [targetId, enabled = 'disabled', color = StrokeColors.gray, sourceYOffset = 0, targetYOffset = 0] =
          target.split(',');
        let targetElement = container.querySelector(`.js-flow-item[data-flow-id="${targetId}"]`);
        if (targetElement != null) {
          let isEnabled = enabled === 'enabled';
          let idBase = `flow_item_${eIndex}_${tIndex}`;
          // get rects then update the cache
          let elementRect = elementRectMap.get(id) ?? element.getBoundingClientRect();
          elementRectMap.set(id, elementRect);
          let targetElementRect = elementRectMap.get(targetId) ?? targetElement.getBoundingClientRect();
          elementRectMap.set(targetId, targetElementRect);

          let stroke = generateStrokeSettings(color as StrokeColors);

          let coordinates = generatePathEdgeCoordinates({
            containerRect,
            elementRect,
            targetElementRect,
            sourceYOffset: Number(sourceYOffset) ?? 0,
            targetYOffset: Number(targetYOffset) ?? 0,
            horizontalLine,
          });

          let path = {
            id: `${idBase}_path`,
            d: generatePathString(coordinates.start, coordinates.end),
            stroke,
          };

          if (isEnabled) {
            paths.push(path);

            let dotSettings = generateDotSettings({
              id: idBase,
              pathCoordinates: coordinates,
              color: stroke.color,
            });

            let dot = { ...dotSettings, pathId: path.id };
            pathDots.push(dot);
          } else {
            // pushes disabled flow lines under enabled
            paths.unshift(path);
          }
        }
      });
    });

    this.paths = paths;
    this.pathDots = pathDots;
  }
}
